import { useState, useEffect } from "react";
import { blogsAPI } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import Modal from "../../components/Modal/Modal";
import BlogForm from "./BlogForm";
import { IBlog } from "../../types";
import "../Apps/Apps.css";
import { useToast } from "../../hooks/useToast";

const BlogsList = () => {
  const { showToast } = useToast();
  const [blogs, setBlogs] = useState<IBlog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<IBlog | null>(null);
  const [filters, setFilters] = useState({
    category: "",
    search: "",
    published: "all", // all, published, draft
    includeDeleted: false,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [limit] = useState(10);

  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage]);

  const fetchBlogs = async () => {
    try {
      setIsLoading(true);

      const params: any = {
        page: currentPage,
        limit: limit,
        includeDeleted: filters.includeDeleted,
      };

      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;

      // Handle published filter
      if (filters.published !== "all") {
        params.published = filters.published === "published";
      }

      console.log("Fetching admin blogs with params:", params);

      // Use admin-specific endpoint
      const response = await blogsAPI.getAllAdmin(params);
      console.log("API Response:", response);

      // Handle Axios response structure
      const actualResponse = response.data;

      if (actualResponse.success) {
        // Check for paginated response
        if (
          actualResponse.data?.data &&
          Array.isArray(actualResponse.data.data)
        ) {
          const { data, pagination } = actualResponse.data;
          setBlogs(data);
          setTotalPages(pagination.totalPages);
          setTotalBlogs(pagination.total);
          setCurrentPage(pagination.page);
        }
        // Check for direct array
        else if (Array.isArray(actualResponse.data)) {
          setBlogs(actualResponse.data);
          setTotalPages(1);
          setTotalBlogs(actualResponse.data.length);
        }
      }

      console.log("Processed blogs:", blogs.length);
    } catch (error: any) {
      console.error("Failed to fetch blogs:", error);
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to fetch blog posts",
      });
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedBlog(null);
    setShowModal(true);
  };

  const handleEdit = (blog: IBlog) => {
    setSelectedBlog(blog);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this blog post?"))
      return;

    try {
      await blogsAPI.delete(id);
      showToast({
        type: "success",
        title: "Success",
        message: "Blog post deleted successfully",
      });
      await fetchBlogs();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to delete blog post",
      });
    }
  };

  const handleRestore = async (id: string) => {
    if (!window.confirm("Are you sure you want to restore this blog post?"))
      return;

    try {
      await blogsAPI.restore(id);
      showToast({
        type: "success",
        title: "Success",
        message: "Blog post restored successfully",
      });
      await fetchBlogs();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to restore blog post",
      });
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to PERMANENTLY delete this blog post? This action cannot be undone!"
      )
    )
      return;

    try {
      await blogsAPI.permanentDelete(id);
      showToast({
        type: "success",
        title: "Success",
        message: "Blog post permanently deleted",
      });
      await fetchBlogs();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to permanently delete blog post",
      });
    }
  };

  const handleSuccess = () => {
    setShowModal(false);
    showToast({
      type: "success",
      title: "Success",
      message: selectedBlog
        ? "Blog post updated successfully"
        : "Blog post created successfully",
    });
    fetchBlogs();
  };

  const handleFormSubmit = async (formData: FormData) => {
    try {
      if (selectedBlog) {
        await blogsAPI.update(selectedBlog._id!, formData);
      } else {
        await blogsAPI.create(formData);
      }
      handleSuccess();
    } catch (error: any) {
      console.error("Failed to save blog:", error);
      showToast({
        type: "error",
        title: "Save failed",
        message: error.message || "Failed to save blog",
      });
    }
  };

  const handleFormCancel = () => {
    setShowModal(false);
    setSelectedBlog(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getAuthorName = (author: string | { name: string }) => {
    if (typeof author === "string") return author;
    return author?.name || "Unknown";
  };

  const columns = [
    {
      key: "featuredImage",
      label: "Image",
      render: (blog: IBlog) => (
        <div className="table-image-container">
          <img
            src={blog.featuredImage}
            alt={blog.title}
            className="table-image"
            onError={(e) => {
              (
                e.target as HTMLImageElement
              ).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                blog.title
              )}&size=200&background=FF9933&color=fff&bold=true`;
            }}
          />
          {blog.isDeleted && (
            <div className="deleted-overlay">
              <span>DELETED</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "title",
      label: "Title",
      render: (blog: IBlog) => (
        <div style={{ maxWidth: "300px" }}>
          <div
            style={{
              fontWeight: 600,
              marginBottom: "0.25rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <span>{blog.title}</span>
            {blog.isDeleted && (
              <span
                style={{
                  padding: "0.25rem 0.75rem",
                  background: "#ef4444",
                  color: "white",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  borderRadius: "50px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Deleted
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: "0.85rem",
              color: "var(--text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {blog.excerpt}
          </div>
        </div>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (blog: IBlog) => (
        <span style={{ textTransform: "capitalize" }}>{blog.category}</span>
      ),
    },
    {
      key: "author",
      label: "Author",
      render: (blog: IBlog) => getAuthorName(blog.author),
    },
    {
      key: "stats",
      label: "Stats",
      render: (blog: IBlog) => (
        <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
          <div>👁️ {blog.views || 0}</div>
          <div>❤️ {blog.likes || 0}</div>
        </div>
      ),
    },
    {
      key: "published",
      label: "Status",
      render: (blog: IBlog) => (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}
        >
          <span
            className={`status-badge ${blog.published ? "published" : "draft"}`}
          >
            {blog.published ? "Published" : "Draft"}
          </span>
          {blog.isDeleted && (
            <span className="status-badge deleted-badge">🗑️ Deleted</span>
          )}
        </div>
      ),
    },
    {
      key: "featured",
      label: "Featured",
      render: (blog: IBlog) => (
        <span className="featured-badge">{blog.featured ? "⭐" : "-"}</span>
      ),
    },
  ];

  // Custom actions for blogs
  const getCustomActions = (blog: IBlog) => {
    if (blog.isDeleted) {
      return (
        <>
          <button
            onClick={() => handleRestore(blog._id!)}
            className="action-btn restore-btn"
            title="Restore"
          >
            ↩️ Restore
          </button>
          <button
            onClick={() => handlePermanentDelete(blog._id!)}
            className="action-btn danger-btn"
            title="Permanently Delete"
          >
            🗑️ Delete Forever
          </button>
        </>
      );
    }
    return null;
  };

  return (
    <div className="blogs-page">
      <div className="page-header">
        <div>
          <h1>Blogs Management</h1>
          <p>Manage your blog posts and articles ({totalBlogs} total)</p>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          <span>➕</span>
          <span>Add New Blog</span>
        </button>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search blogs..."
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="filter-group">
          <label>Category</label>
          <select
            value={filters.category}
            onChange={(e) => {
              setFilters({ ...filters, category: e.target.value });
              setCurrentPage(1);
            }}
          >
            <option value="">All Categories</option>
            <option value="technology">Technology</option>
            <option value="business">Business</option>
            <option value="innovation">Innovation</option>
            <option value="healthcare">Healthcare</option>
            <option value="energy">Energy</option>
            <option value="training">Training</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.published}
            onChange={(e) => {
              setFilters({ ...filters, published: e.target.value });
              setCurrentPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={filters.includeDeleted}
              onChange={(e) => {
                setFilters({ ...filters, includeDeleted: e.target.checked });
                setCurrentPage(1);
              }}
            />
            <span>Show Deleted</span>
          </label>
        </div>
      </div>

      <DataTable
        data={blogs}
        columns={columns}
        isLoading={isLoading}
        onEdit={(row: IBlog) => {
          if (!row.isDeleted) {
            handleEdit(row);
          }
        }}
        onDelete={(id: string) => {
          const blog = blogs.find((b) => b._id === id);
          if (blog && !blog.isDeleted) {
            handleDelete(id);
          }
        }}
        customActions={getCustomActions}
        emptyMessage="No blogs found. Create your first blog post!"
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
          >
            ← Previous
          </button>

          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalBlogs} total blogs)
          </div>

          <div className="pagination-numbers">
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNumber: number;
              if (totalPages <= 7) {
                pageNumber = i + 1;
              } else if (currentPage <= 4) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 3) {
                pageNumber = totalPages - 6 + i;
              } else {
                pageNumber = currentPage - 3 + i;
              }

              return (
                <button
                  key={pageNumber}
                  className={`pagination-number ${
                    currentPage === pageNumber ? "active" : ""
                  }`}
                  onClick={() => handlePageChange(pageNumber)}
                  disabled={isLoading}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
          >
            Next →
          </button>
        </div>
      )}

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={selectedBlog ? "Edit Blog Post" : "Create New Blog Post"}
        >
          <BlogForm
            blog={selectedBlog}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </Modal>
      )}

      <style>{`
        .table-image-container {
          position: relative;
          display: inline-block;
        }

        .deleted-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(239, 68, 68, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
          font-size: 0.75rem;
          letter-spacing: 1px;
        }

        .deleted-badge {
          background: #ef4444 !important;
          color: white !important;
          font-size: 0.75rem;
          padding: 0.35rem 0.75rem;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 2rem;
          flex-wrap: wrap;
        }

        .pagination-btn {
          padding: 0.75rem 1.5rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: var(--lico-orange);
          background: var(--lico-orange);
          color: white;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          padding: 0.75rem 1rem;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .pagination-numbers {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        .pagination-number {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-secondary);
          border: 2px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .pagination-number:hover:not(:disabled) {
          border-color: var(--lico-orange);
          color: var(--lico-orange);
        }

        .pagination-number.active {
          background: var(--lico-orange);
          border-color: var(--lico-orange);
          color: white;
        }

        .pagination-number:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .restore-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .restore-btn:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .danger-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .danger-btn:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          padding: 0.5rem 0;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default BlogsList;
